import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  MOMENT_DATEPICKER_PROVIDERS,
  NgDialogModule,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import { resolveInvoiceDueDate } from '@principle-theorem/principle-core';
import {
  type IInvoice,
  InvoiceStatus,
} from '@principle-theorem/principle-core/interfaces';
import { type WithRef, toMoment } from '@principle-theorem/shared';
import { type Moment } from 'moment-timezone';

export interface IEditDueDateDialogData {
  invoice: WithRef<IInvoice>;
  currentDueDate?: Moment;
}

export interface IEditDueDateDialogResult {
  dueDate?: Moment;
}

@Component({
  selector: 'pr-edit-due-date-dialog',
  templateUrl: './edit-due-date-dialog.component.html',
  styleUrls: ['./edit-due-date-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgDialogModule, MatDatepickerModule],
  providers: [...MOMENT_DATEPICKER_PROVIDERS],
})
export class EditDueDateDialogComponent {
  private _dialogRef = inject(
    MatDialogRef<EditDueDateDialogComponent, IEditDueDateDialogResult>
  );
  data = inject<IEditDueDateDialogData>(MAT_DIALOG_DATA);
  dueDateCtrl: TypedFormControl<Moment | undefined>;
  isDraft: boolean;
  isResetting = false;

  constructor() {
    this.isDraft = this.data.invoice.status === InvoiceStatus.Draft;
    this.dueDateCtrl = new TypedFormControl<Moment | undefined>(
      this.data.currentDueDate
    );
  }

  async resetToDefault(): Promise<void> {
    if (this.isDraft) {
      this.dueDateCtrl.setValue(undefined);
      return;
    }
    this.isResetting = true;
    try {
      const invoice = this.data.invoice;
      const defaultDueDate = invoice.issuedAt
        ? await resolveInvoiceDueDate(
            invoice,
            undefined,
            toMoment(invoice.issuedAt)
          )
        : await resolveInvoiceDueDate(invoice);
      this.dueDateCtrl.setValue(defaultDueDate);
    } finally {
      this.isResetting = false;
    }
  }

  submit(): void {
    this._dialogRef.close({ dueDate: this.dueDateCtrl.value ?? undefined });
  }
}
